import { getInitials } from '@tempo-io/string-util';
import { Avatar, AvatarFallback, AvatarImage } from '@tempo-io/sui-avatar';
import { Button } from '@tempo-io/sui-button';
import { ExternalLink } from '@tempo-io/sui-icons';
import { useTranslation } from 'react-i18next';
import { EmptyFieldValue } from '@/components/common/item-field-value/EmptyFieldValue';
import { DateFormat, formatLocalDate } from '@/lib/utils/common/dateFormats';
import type { SystemFieldsTabAdapter } from '../adapters/types';
import { useTabAdapter } from '../context/TabAdapterContext';
import { useOrgMemberByIdQueryWithPublished } from '../hooks/useOrgMemberByIdQueryWithPublished';
import { useUserProfileQueryWithPublished } from '../hooks/useUserProfileQueryWithPublished';

export const SystemFields = () => {
  const { t } = useTranslation();
  const adapter = useTabAdapter<SystemFieldsTabAdapter>();
  const createdBy = adapter.getFieldValue('createdBy');
  const createdAt = adapter.getFieldValue('createdAt');
  const updatedBy = adapter.getFieldValue('updatedBy');
  const updatedAt = adapter.getFieldValue('updatedAt');
  const doneDateTime = adapter.getFieldValue('doneDateTime');
  const externalKey = adapter.getFieldValue('externalKey');
  const externalLink = adapter.getFieldValue('externalLink');
  const { data: createdByUser } = useOrgMemberByIdQueryWithPublished(createdBy);
  const { data: updatedByUser } = useOrgMemberByIdQueryWithPublished(updatedBy);
  const { data: currentUser } = useUserProfileQueryWithPublished();
  const formatDate = (dateString: string | null | undefined) => {
    return formatLocalDate(dateString ?? '', DateFormat.MONTH_LONG_DAY_SHORT_YEAR_TIME);
  };

  const createdByDisplayName = createdByUser?.displayLabel ?? createdBy;
  const updatedByDisplayName = updatedByUser?.displayLabel ?? updatedBy;

  return (
    <div className="flex cursor-default flex-col gap-4">
      <div className="min-w-0 truncate text-base font-medium leading-5 tracking-normal text-text-brand-primary">
        {t(($) => $.adaptivePlanner.itemPanel.systemFields)}
      </div>
      <div className="flex flex-col gap-4">
        {adapter.isVisible('createdBy') && (
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">
              {t(($) => $.adaptivePlanner.itemPanel.systemField.createdBy)}:
            </span>
            {createdByUser?.displayLabel ? (
              <>
                <Avatar className="size-5">
                  <AvatarImage
                    src={createdByUser?.pictureUrl ?? undefined}
                    alt={createdByUser?.displayLabel || ''}
                  />
                  <AvatarFallback>{getInitials(createdByUser?.displayLabel)}</AvatarFallback>
                </Avatar>
                <span className="text-sm text-text-neutral-primary">
                  {createdByUser.displayLabel}{' '}
                  {currentUser?.id === createdBy
                    ? t(($) => $.adaptivePlanner.itemPanel.systemField.you)
                    : ''}
                </span>
              </>
            ) : createdByDisplayName ? (
              <span className="text-sm text-text-neutral-primary">{createdByDisplayName}</span>
            ) : (
              <EmptyFieldValue />
            )}
          </div>
        )}

        {adapter.isVisible('createdAt') && (
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">
              {t(($) => $.adaptivePlanner.itemPanel.systemField.createdOn)}:
            </span>
            {createdAt ? (
              <span className="text-sm text-text-neutral-primary">{formatDate(createdAt)}</span>
            ) : (
              <EmptyFieldValue />
            )}
          </div>
        )}

        {adapter.isVisible('updatedBy') && (
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">
              {t(($) => $.adaptivePlanner.itemPanel.systemField.updatedBy)}:
            </span>
            {updatedByUser?.displayLabel ? (
              <>
                <Avatar className="size-5">
                  <AvatarImage
                    src={updatedByUser?.pictureUrl ?? undefined}
                    alt={updatedByUser?.displayLabel || ''}
                  />
                  <AvatarFallback>{getInitials(updatedByUser?.displayLabel)}</AvatarFallback>
                </Avatar>
                <span className="text-sm text-text-neutral-primary">
                  {updatedByUser.displayLabel}
                </span>
              </>
            ) : updatedByDisplayName ? (
              <span className="text-sm text-text-neutral-primary">{updatedByDisplayName}</span>
            ) : (
              <EmptyFieldValue />
            )}
          </div>
        )}

        {adapter.isVisible('updatedAt') && (
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">
              {t(($) => $.adaptivePlanner.itemPanel.systemField.updatedOn)}:
            </span>
            {updatedAt ? (
              <span className="text-sm text-text-neutral-primary">{formatDate(updatedAt)}</span>
            ) : (
              <EmptyFieldValue />
            )}
          </div>
        )}

        {adapter.isVisible('doneDateTime') && (
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">
              {t(($) => $.adaptivePlanner.itemPanel.systemField.doneDate)}:
            </span>
            {doneDateTime ? (
              <span className="text-sm text-text-neutral-primary">{formatDate(doneDateTime)}</span>
            ) : (
              <EmptyFieldValue />
            )}
          </div>
        )}

        {adapter.isVisible('externalKey') && (
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">
              {t(($) => $.adaptivePlanner.itemPanel.systemField.externalKey)}:
            </span>
            {externalKey && externalLink ? (
              <a
                href={externalLink}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2"
              >
                <Button
                  size="sm"
                  variant="secondary"
                  aria-label={t(($) => $.adaptivePlanner.itemPanel.systemField.openExternalLink)}
                >
                  {externalKey}
                </Button>
                <ExternalLink className="cursor-pointer" />
              </a>
            ) : externalKey ? (
              <span className="text-sm text-text-neutral-primary">{externalKey}</span>
            ) : (
              <EmptyFieldValue />
            )}
          </div>
        )}
      </div>
    </div>
  );
};
